import { CustomerReviewWithProcName } from "../../../domain/customer-review/customer-review";
import { ProcedureEntity } from "../../../domain/procedures/procedure-entity";
import { rebuildQuery } from "../../../../utils/url-utils";
import apiClient from "../../../apis/api-client";
import {
  DefaultNumItemsPerPages,
  toTotalPage,
} from "../../../domain/pagination/paging";
import { toast } from "react-toastify";
import { formatDate } from "../../../../utils/date-utils";
import FileSaver from "file-saver";
import {
  reportUnexpectedError,
  BaseApplicationService,
  SmartApplyApiError,
  nameCsvFile,
  nameCsvFileWithTerm,
} from "@graffer-inc/smart-apply";

export type CustomerReviewsContextProps = {
  isLoading: boolean;
  customerReviews: Array<CustomerReviewWithProcName>;
  paging: {
    current: number;
    total: number;
  };
  dateRange: {
    from?: Date;
    to?: Date;
  };
  setPage: typeof CustomerReviewsService.prototype.setPage;
  movePage: typeof CustomerReviewsService.prototype.movePage;
  setDateRange: typeof CustomerReviewsService.prototype.setDateRange;
  selectedProcedure?: ProcedureEntity;
  fetchCustomerReviews: typeof CustomerReviewsService.prototype.fetchCustomerReviews;
  searchCustomerReviews: typeof CustomerReviewsService.prototype.searchCustomerReviews;
  onCSVDownload: typeof CustomerReviewsService.prototype.onCSVDownload;
  onSelectProcedure: typeof CustomerReviewsService.prototype.onSelectProcedure;
};

export class CustomerReviewsService extends BaseApplicationService<CustomerReviewsContextProps> {
  private readonly customerReviewCountPerPage: number = DefaultNumItemsPerPages;

  state: CustomerReviewsContextProps = {
    isLoading: false,
    customerReviews: Array<CustomerReviewWithProcName>(),
    paging: {
      current: 1,
      total: 0,
    },
    dateRange: {
      from: undefined,
      to: undefined,
    },
    selectedProcedure: undefined as ProcedureEntity | undefined,
    setPage: this.setPage.bind(this),
    movePage: this.movePage.bind(this),
    setDateRange: this.setDateRange.bind(this),
    fetchCustomerReviews: this.fetchCustomerReviews.bind(this),
    searchCustomerReviews: this.searchCustomerReviews.bind(this),
    onCSVDownload: this.onCSVDownload.bind(this),
    onSelectProcedure: this.onSelectProcedure.bind(this),
  };

  setPage(p: number) {
    this.setState({
      paging: {
        ...this.state.paging,
        current: p,
      },
    });
  }

  movePage(p: number) {
    this.history.push(
      rebuildQuery({
        page: p.toString(),
      }),
    );
  }

  setDateRange(dateRange: { from?: Date; to?: Date }) {
    this.setState({
      dateRange,
    });
  }

  setIsLoading(isLoading: boolean) {
    this.setState({ isLoading });
  }

  searchCustomerReviews() {
    const date = this.state.dateRange;
    const fromStr = date.from ? formatDate(date.from as Date) : "";
    const toStr = date.to ? formatDate(date.to as Date) : "";
    this.history.replace(
      rebuildQuery({
        page: "1",
        from: fromStr,
        to: toStr,
      }),
    );
  }

  async fetchCustomerReviews(procedureId: string | undefined) {
    const { current: p } = this.state.paging;
    const { dateRange } = this.state;

    this.setIsLoading(true);

    try {
      const { data: reviews, headers } = await apiClient.fetchCustomerReviews(
        p,
        this.customerReviewCountPerPage,
        {
          ...dateRange,
          procedureId: procedureId!,
        },
      );

      this.setState({
        paging: {
          ...this.state.paging,
          total: toTotalPage(
            Number(headers["pagination-count"]),
            this.customerReviewCountPerPage,
          ),
        },
        customerReviews: reviews || [],
      });
    } catch (e) {
      const apiError = new SmartApplyApiError(e);

      if (SmartApplyApiError.is403(apiError)) {
        toast.error("申請者レビューを取得する権限がありません。");
        return;
      }

      reportUnexpectedError(e);
      toast.error("申請者レビューの取得に失敗しました。");
    } finally {
      this.setIsLoading(false);
    }
  }

  async onSelectProcedure(procedureId?: string) {
    try {
      let fetchedProcedure: ProcedureEntity | undefined = undefined;
      if (procedureId) {
        const { data: procedure } = await apiClient.fetchProcedure(
          procedureId!,
        );
        fetchedProcedure = procedure;
      }
      this.setState({
        selectedProcedure: fetchedProcedure,
      });
      this.history.replace(
        rebuildQuery({
          procedureId: procedureId ?? "",
        }),
      );
    } catch (e) {
      if (SmartApplyApiError.is403(e)) {
        toast.error("手続きを取得する権限がありません。");
        return;
      }
      reportUnexpectedError(e);
      toast.error("手続きの取得に失敗しました。");
    } finally {
      this.setIsLoading(false);
    }
  }

  async onCSVDownload() {
    const dates: { from?: Date; to?: Date } = this.state.dateRange;
    const procedureId: string | undefined = this.state.selectedProcedure?.id;
    try {
      const { data } = await apiClient.downloadCustomerReviewsCSV({
        ...dates,
        procedureId,
      });

      FileSaver.saveAs(
        data,
        this.createFileName(procedureId, dates.from, dates.to),
      );
    } catch (e) {
      this.handleCsvDownloadError(e);
    }
  }

  createFileName(
    procedureId: string | undefined,
    from: Date | undefined,
    to: Date | undefined,
  ) {
    let fileName = "customer-reviews";
    if (procedureId) {
      fileName = fileName + `_procedure-id-${procedureId}`;
    }
    if (from || to) {
      return nameCsvFileWithTerm(fileName, { from: from, to: to });
    }
    return nameCsvFile(fileName);
  }

  async handleCsvDownloadError(e: any) {
    const apiError = new SmartApplyApiError(e);
    if (SmartApplyApiError.is403(apiError)) {
      toast.error("申請者レビューをダウンロードする権限がありません。");
      return;
    }

    await apiError.resolveErrorResponse();
    switch (apiError.errorCode()) {
      case "bad_request.too_large_record_count":
        toast.error(
          "対象の申請者レビュー件数が出力上限を超えました。指定期間を狭めてください。",
        );
        return;
    }
    reportUnexpectedError(e);
    toast.error("CSVダウンロードでエラーが発生しました");
  }
}
